import { Relation, Ty } from '@cotera/era';
import { Table, Vector, Schema as ArrowSchema } from 'apache-arrow';
import { z } from 'zod/v3';
import { Assert, isContiguousSlice } from '@cotera/utilities';
import { processVal, rowMapper } from './map-arrow-to-era-types';
import _ from 'lodash';
export class DuckDBQueryResult<T extends Ty.Row = Ty.Row> {
  constructor(
    protected readonly arrowTable: Table,
    readonly source: Record<string, Ty.ExtendedAttributeType>,
    readonly schema?: z.ZodSchema
  ) {}

  getArrowTable(): Table {
    return this.arrowTable;
  }

  toArrayOf<T extends z.ZodSchema>(schema: T): z.infer<z.ZodArray<T>> {
    return z.array(schema).parse(this.toArray());
  }

  toString() {
    return this.arrowTable.toString();
  }

  toArray(opts?: {
    selectedColumns?: string[];
    selectedIndices?: number[];
  }): T[] {
    const [selectedColumns, selectedIndices] = parseToArrayOpts(opts);

    const selectedSource =
      selectedColumns === null
        ? this.source
        : _.pick(this.source, selectedColumns);

    const toTyArray = (arrowTable: Table) =>
      arrowTable.toArray().map((r) => rowMapper(selectedSource)(r) as T);

    const selectedArrowTableColumns =
      selectedColumns === null
        ? this.arrowTable
        : this.arrowTable.select(selectedColumns);

    if (selectedIndices === null) {
      return toTyArray(selectedArrowTableColumns);
    }

    const continuity = isContiguousSlice(selectedIndices);

    if (continuity.isContiguous) {
      const { beginInclusive, endExclusive } = continuity;
      return toTyArray(
        selectedArrowTableColumns.slice(beginInclusive, endExclusive)
      );
    } else {
      // Array of 1-row tables (of the rows we want)
      const arrowTables: Table[] = selectedIndices.map((index) =>
        selectedArrowTableColumns.slice(index, index + 1)
      );

      return arrowTables.flatMap(toTyArray);
    }
  }

  get length() {
    return this.arrowTable.numRows;
  }

  slice(start: number, stop: number): DuckDBQueryResult {
    return new DuckDBQueryResult(
      this.arrowTable.slice(start, stop),
      this.source
    );
  }

  row(index: number): DuckDBQueryResult {
    return new DuckDBQueryResult(
      this.arrowTable.slice(index, index + 1),
      this.source
    );
  }

  concat(other: DuckDBQueryResult): DuckDBQueryResult {
    return new DuckDBQueryResult(
      this.arrowTable.concat(other.arrowTable),
      this.source
    );
  }

  column<Schema extends z.Schema>(name: string): Column<Schema> {
    return new Column(
      name,
      this.source[name]!,
      this.arrowTable.schema,
      this.arrowTable.getChild(name)
    );
  }

  get numRows(): number {
    return this.arrowTable.numRows;
  }

  get numCols(): number {
    return this.arrowTable.numCols;
  }

  isEqual(other: DuckDBQueryResult): boolean {
    if (this.source instanceof Relation && other?.source instanceof Relation) {
      return this.source.sqlHash() === other.source.sqlHash();
    }

    return this === other;
  }
}

class Column<Schema extends z.ZodSchema> {
  constructor(
    readonly name: string,
    readonly ty: Ty.ExtendedAttributeType,
    readonly schema: ArrowSchema,
    private readonly column: Vector<any> | null
  ) {}

  get length(): number {
    return this.column?.length ?? 0;
  }

  valueAt(index: number): z.infer<Schema> {
    const arrowFields = this.schema.fields;
    const field = arrowFields.find((x) => x.name === this.name);
    Assert.assert(
      field !== undefined,
      `Field ${this.name} not found in schema`
    );

    const value = processVal(this.ty)(this.column?.get(Number(index)));
    return value as z.infer<Schema>;
  }

  rawValueAt(index: number): any {
    return this.column?.get(index);
  }
}

function parseToArrayOpts(opts?: {
  selectedColumns?: string[];
  selectedIndices?: number[];
}): [selectedColumns: string[] | null, selectedIndices: number[] | null] {
  if (opts === undefined) {
    return [null, null];
  }

  const selectedColumns =
    opts.selectedColumns === undefined ? null : opts.selectedColumns;

  const selectedIndices =
    opts.selectedIndices === undefined ? null : opts.selectedIndices;

  return [selectedColumns, selectedIndices];
}
